import React, { useRef, useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useIntl } from "react-intl";

import "./NewWorkspaceLayout.scss";
import { workspaceSelectors } from "../../shared/layout/store/workspaceReducer";
import { authSelectors } from "../../auth/store/authReducer";
import { loadWorkspaces } from "../../shared/layout/store/workspaceActionCreators";

import Message from "../../../i18n/Message";
import UserCircleIcon from "../../../assets/icons/UserCircleIcon";
import SettingsIcon from "../../../assets/icons/SettignsIcon";
import ArrowOutIcon from "../../../assets/icons/ArrowOutIcon";
import UsersIcon from "../../../assets/icons/UsersIcon";
import CreditCardIcon from "../../../assets/icons/CreditCardIcon";
import WorkspaceDropdown from "./WorkspaceDropdown";
import {
  linkToPrompts,
  linkToNewChats,
  linkToVendorKeys,
  linkToAccountSettings,
  linkToSubscription,
  linkToTeam,
  linkToNewFlows,
  linkToNewPrompts,
  linkToWorkspaceSettings,
  linkToToolPlugins,
  linkToKnowledgeBases,
  linkToAssistants,
  linkToIntegrations,
  linkToGateways,
  linkToBuilder,
} from "../../../routes";
import { isTeamAdmin } from "../../../backend/memberRole";
import { logout } from "../../auth/store/actionCreators";
import FileTextIcon from "../../../assets/icons/FileTextIcon";
import FlowsIcon from "../../../assets/icons/FlowsIcon";
import GridIcon from "../../../assets/icons/GridIcon";
import FastspringIntegration from "../../shared/FastspringIntegration";
import TrialExpiredDialog from "../../shared/trial-expired/TrialExpiredDialog";
import {
  BadgeInfoIcon,
  BlocksIcon,
  BookAIcon,
  BotIcon,
  CableIcon,
  KeyRoundIcon,
  LibraryBigIcon,
  MessageSquareTextIcon,
  NetworkIcon,
  Package2Icon,
  PocketKnifeIcon,
  PuzzleIcon,
  WandSparklesIcon,
} from "lucide-react";

const NewWorkspaceLayout = ({ children, contentPanelClassName = "" }) => {
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const intl = useIntl();
  const { workspaceId } = useParams();
  const workspaces = useSelector(workspaceSelectors.getWorkspaces);
  const workspace = useSelector(workspaceSelectors.getCurrent(workspaceId));
  const user = useSelector(authSelectors.getUser);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const headerMenuRef = useRef(null);
  const submenuRef = useRef(null);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(
    window.innerWidth < 1505
  );
  const [wasCollapsed, setWasCollapsed] = useState(false);
  const [showTrialExpired, setShowTrialExpired] = useState(false);

  useEffect(() => {
    if (!workspaces) {
      dispatch(loadWorkspaces(intl));
    }
    if (workspaces && workspaces.length > 0 && !workspace && !workspaceId) {
      navigate(linkToNewChats(workspaces[0].uid));
    }
  }, [workspaces, dispatch, workspace, workspaceId, navigate, intl]);

  useEffect(() => {
    if (showHeaderMenu) {
      console.log("Adding onclick handler;");
      const onClickAway = (e) => {
        if (
          submenuRef.current &&
          e.target !== submenuRef.current &&
          !submenuRef.current.contains(e.target)
        ) {
          console.log("Hiding header menu", e.target);
          setShowHeaderMenu(false);
        }
      };
      document.addEventListener("click", onClickAway);
      return () => document.removeEventListener("click", onClickAway);
    }
  }, [showHeaderMenu, submenuRef]);

  const trialDaysLeft = user.subscription.daysLeft;

  useEffect(() => {
    if (trialDaysLeft === 0) {
      setShowTrialExpired(true);
    }
  }, [trialDaysLeft, setShowTrialExpired]);

  let trialSummaryClass = "";
  let trialButtonClass = "";
  if (trialDaysLeft < 3) {
    trialSummaryClass = "expired";
  } else if (trialDaysLeft < 8) {
    trialSummaryClass = "soon";
  }

  const onLeftPanelMouseOver = () => {
    if (leftPanelCollapsed) {
      setLeftPanelCollapsed(false);
      setWasCollapsed(true);
    }
  };

  const onLeftPanelMouseLeave = () => {
    if (wasCollapsed) {
      setWasCollapsed(false);
      setLeftPanelCollapsed(true);
    }
  };
  //const isSdk = window.location.href.indexOf("/app/sdk") !== -1;

  const LogoWrapper = ({ children }) => <a href={"/app"}>{children}</a>;

  return (
    <div className="workspace_layout">
      <div
        className={
          "main_layout " + (leftPanelCollapsed ? "left_menu_collapsed" : "")
        }
      >
        <header>
          <div className="logo">
            <LogoWrapper>
              <img
                src="/images/promptleo-logo-grey.svg"
                width="190"
                alt="Promptleo"
              />
            </LogoWrapper>
          </div>
          {
            <>
              <div className="header_menu" ref={headerMenuRef}>
                {user.subscription.plan !== "free" &&
                  (user.subscription.trialInProgress ||
                    user.subscription.daysLeft === 0) && (
                    <div className="item hidden-xs">
                      <div className={"trial_summary " + trialSummaryClass}>
                        <div className="trial_text">
                          <span className="days_highlighted">
                            {trialDaysLeft >= 1 ? (
                              <Message
                                id="trial_days_left"
                                values={{ count: trialDaysLeft }}
                              />
                            ) : (
                              <Message id="trial_expired" />
                            )}
                          </span>
                          .
                          <Message id="trial_summary_text" />
                        </div>
                        <div className="trial_button">
                          <button
                            onClick={() =>
                              window.showPaymentWindow(
                                user,
                                user.subscription.planCode
                              )
                            }
                            className={
                              "btn btn-sm btn-outline-primary " +
                              trialButtonClass
                            }
                          >
                            <Message id="add_card" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                <div className="item">
                  <div
                    className="entry"
                    onClick={() => navigate(linkToNewChats(workspaceId))}
                  >
                    <GridIcon color="rgb(52, 71, 103)" size={20} />
                    <Message id="menu_workspaces" />
                  </div>
                </div>
                <div className="item">
                  <div
                    className="entry"
                    onClick={() => navigate(linkToPrompts(workspaceId))}
                  >
                    <Package2Icon color="rgb(52, 71, 103)" size={20} />
                    <Message id="menu_legacy" />
                  </div>
                </div>
                <div className="item">
                  <div
                    className="entry"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowHeaderMenu(!showHeaderMenu);
                    }}
                  >
                    <UserCircleIcon color="#344767" strokeWidth="1.5" />
                    <span className="hidden-xs">{user ? user.name : ""}</span>
                  </div>
                  <div className="submenu_container">
                    {showHeaderMenu && (
                      <div className="submenu" ref={submenuRef}>
                        <div onClick={() => navigate(linkToAccountSettings())}>
                          <SettingsIcon color="#333" size="20" />{" "}
                          <Message id="account" />
                        </div>
                        <div onClick={() => navigate(linkToSubscription())}>
                          <CreditCardIcon color="#333" size="20" />{" "}
                          <Message id="plan" />
                        </div>
                        {isTeamAdmin(user) && (
                          <>
                            <div onClick={() => navigate(linkToTeam())}>
                              <UsersIcon color="#333" size="20" />{" "}
                              <Message id="users" />
                            </div>
                          </>
                        )}
                        <div onClick={() => dispatch(logout())}>
                          <ArrowOutIcon color="#333" size="20" />{" "}
                          <Message id="menu_logout" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="break"></div>
              <div className="mobile_trial visible-xs-flex">
                {/*user.subscription.plan !== "free" &&
                  (user.subscription.trialInProgress ||
                    user.subscription.daysLeft === 0) && (
                    <div className="item">
                      <div className={"trial_summary " + trialSummaryClass}>
                        <div className="trial_text">
                          <span className="days_highlighted">
                            {trialDaysLeft >= 1 ? (
                              <Message
                                id="trial_days_left"
                                values={{ count: trialDaysLeft }}
                              />
                            ) : (
                              <Message id="trial_expired" />
                            )}
                          </span>
                          .
                          <Message id="trial_summary_text" />
                        </div>
                        <div className="trial_button">
                          <button
                            onClick={() =>
                              window.showPaymentWindow(
                                user,
                                user.subscription.planCode
                              )
                            }
                            className={"app-btn " + trialButtonClass}
                          >
                            <Message id="add_card" />
                          </button>
                        </div>
                      </div>
                    </div>
                          )*/}
              </div>
            </>
          }
        </header>
        <div className="left_panel_mobile visible-xs">
          <WorkspaceDropdown
            workspace={workspace}
            workspaces={workspaces}
            user={user}
          />
          <div className="space_menu_mobile">
            <NavLink
              to={linkToWorkspaceSettings(workspaceId)}
              className={(navData) => (navData.isActive ? "selected" : "")}
            >
              <SettingsIcon color="#383838" strokeWidth="1.5" />
            </NavLink>
          </div>
        </div>
        <div
          className={
            "left_panel hidden-xs " + (leftPanelCollapsed ? "collapsed" : "")
          }
          onMouseOver={onLeftPanelMouseOver}
          onMouseLeave={onLeftPanelMouseLeave}
        >
          <hr className="horizontal dark"></hr>
          <WorkspaceDropdown
            workspace={workspace}
            workspaces={workspaces}
            user={user}
          />
          <div className="sidebar-items">
            <NavLink
              to={linkToNewChats(workspaceId)}
              className={(navData) => (navData.isActive ? "selected" : "")}
            >
              <div className="icon_wrapper">
                {/*<MessageSquareIcon color="#67748e" size="18" />*/}
                <MessageSquareTextIcon color="#67748e" size="18" />
              </div>
              <span>
                <Message id="menu_chats" />
              </span>
            </NavLink>

            <NavLink
              to={linkToNewPrompts(workspaceId)}
              className={(navData) => (navData.isActive ? "selected" : "")}
            >
              <div className="icon_wrapper">
                <FileTextIcon color="#67748e" size="18" />
              </div>
              <span>
                <Message id="menu_prompts" />
              </span>
            </NavLink>

            <NavLink
              to={linkToToolPlugins(workspaceId)}
              className={(navData) => (navData.isActive ? "selected" : "")}
            >
              <div className="icon_wrapper">
                <PuzzleIcon color="#67748e" size="18" />
              </div>
              <span>
                <Message id="menu_tool_plugins" />
              </span>
            </NavLink>

            <NavLink
              to={linkToKnowledgeBases(workspaceId)}
              className={(navData) => (navData.isActive ? "selected" : "")}
            >
              <div className="icon_wrapper">
                <LibraryBigIcon color="#67748e" size="18" />
              </div>
              <span>
                <Message id="menu_knowledge_bases" />
              </span>
            </NavLink>

            <NavLink
              to={linkToAssistants(workspaceId)}
              className={(navData) => (navData.isActive ? "selected" : "")}
            >
              <div className="icon_wrapper">
                <BotIcon color="#67748e" size="18" />
              </div>
              <span>
                <Message id="menu_agents" />
              </span>
            </NavLink>

            <NavLink
              to={linkToBuilder(workspaceId)}
              className={(navData) => (navData.isActive ? "selected" : "")}
            >
              <div className="icon_wrapper">
                <WandSparklesIcon color="#67748e" size="18" />
              </div>
              <span>
                <Message id="menu_builder" />
              </span>
            </NavLink>

            <NavLink
              to={linkToGateways(workspaceId)}
              className={(navData) => (navData.isActive ? "selected" : "")}
            >
              <div className="icon_wrapper">
                <NetworkIcon color="#67748e" size="18" />
              </div>
              <span>
                <Message id="menu_gateways" />
              </span>
            </NavLink>

            <NavLink
              to={linkToVendorKeys(workspaceId)}
              className={(navData) => (navData.isActive ? "selected" : "")}
            >
              <div className="icon_wrapper">
                <KeyRoundIcon color="#67748e" size="18" />
              </div>
              <span>
                <Message id="menu_vendor_keys" />
              </span>
            </NavLink>

            <NavLink
              to={linkToIntegrations(workspaceId)}
              className={(navData) => (navData.isActive ? "selected" : "")}
            >
              <div className="icon_wrapper">
                <CableIcon color="#67748e" size="18" />
              </div>
              <span>
                <Message id="menu_integrations" />
              </span>
            </NavLink>

            <NavLink
              to={linkToWorkspaceSettings(workspaceId)}
              className={(navData) => (navData.isActive ? "selected" : "")}
            >
              <div className="icon_wrapper">
                <SettingsIcon color="#67748e" size="18" />
              </div>
              <span>
                <Message id="settings" />
              </span>
            </NavLink>
          </div>
          {/*<div className="collapse_control">
            {leftPanelCollapsed || wasCollapsed ? (
              <button
                type="button"
                className="no-btn"
                onClick={() => {
                  setWasCollapsed(false);
                  setLeftPanelCollapsed(false);
                }}
              >
                <ChevronRightIcon color="#383838" />
                <span>
                  <Message id="menu_expand" />
                </span>
              </button>
            ) : (
              <button type="button" className="no-btn" onClick={() => setLeftPanelCollapsed(true)}>
                <ChevronLeftIcon color="#383838" />
                <Message id="collapse" />
              </button>
            )}
            </div>*/}
        </div>
        <div className={`content_panel ${contentPanelClassName}`}>
          {children}
        </div>
      </div>
      <ToastContainer
        closeButton={true}
        className="notification-box"
        position="top-right"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
      />
      <FastspringIntegration />
      {showTrialExpired && <TrialExpiredDialog user={user} />}
    </div>
  );
};

export default NewWorkspaceLayout;
